import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { dashboardLogin } from "./apiCalls";
import { extractAuthToken } from "./authenticationService";
import { setDashboardApiToken } from "./apiSessionService";

/** Token is refreshed 10 minutes before it expires (50-minute window). */
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;
const MAX_ATTEMPTS = 5;
const RETRY_DELAY_MS = 600;

interface DashboardLoginState {
  /** Whether a login request is currently in-flight. */
  active: boolean;
  /** How many consecutive failures have occurred. */
  failedAttempts: number;
  /** Locked after MAX_ATTEMPTS failures — no further automatic retries. */
  locked: boolean;
}

interface DashboardLoginContextValue {
  state: DashboardLoginState;
  /** Manually trigger a login (e.g. after token expiry detected by a query). */
  triggerLogin: () => void;
}

const DashboardLoginContext = createContext<DashboardLoginContextValue | undefined>(
  undefined
);

export function useDashboardLogin() {
  const ctx = useContext(DashboardLoginContext);
  if (!ctx) throw new Error("useDashboardLogin must be used inside DashboardLoginProvider");
  return ctx;
}

export function DashboardLoginProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DashboardLoginState>({
    active: false,
    failedAttempts: 0,
    locked: false,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const attemptRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleNext = useCallback((delay: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runLogin(), delay);
  }, []);

  const runLogin = useCallback(async () => {
    const current = stateRef.current;
    if (current.active || current.locked) return;

    setState((prev) => ({ ...prev, active: true }));

    let attempts = 0;
    let lastError: unknown;

    // Up to MAX_ATTEMPTS tries for the dashboard login specifically
    while (attempts < MAX_ATTEMPTS) {
      try {
        const res = await dashboardLogin();
        const token = extractAuthToken(res);
        if (token) {
          setDashboardApiToken(token);
          attemptRef.current = 0;
          setState({ active: false, failedAttempts: 0, locked: false });
          // Schedule next refresh at the 50-minute mark
          scheduleNext(REFRESH_INTERVAL_MS);
          return;
        }
        lastError = new Error("Dashboard login returned no token");
      } catch (e) {
        lastError = e;
      }
      attempts++;
      if (attempts < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempts));
      }
    }

    // All attempts exhausted
    console.error("Dashboard login failed after", MAX_ATTEMPTS, "attempts", lastError);
    toast.error("Unable to reach the server after 5 attempts. Please refresh the page.");
    setState({ active: false, failedAttempts: MAX_ATTEMPTS, locked: true });
  }, [scheduleNext]);

  // On mount: run login immediately + handle visibility change (browser tab re-open)
  useEffect(() => {
    runLogin();

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && !stateRef.current.locked) {
        runLogin();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const triggerLogin = useCallback(() => {
    if (!stateRef.current.active && !stateRef.current.locked) {
      setState((prev) => ({ ...prev, failedAttempts: 0, locked: false }));
      runLogin();
    }
  }, [runLogin]);

  return (
    <DashboardLoginContext.Provider value={{ state, triggerLogin }}>
      {children}
    </DashboardLoginContext.Provider>
  );
}
